/**
 * block-vip-pricing.js
 *
 * Handles the "Add Membership & Unlock Pricing" button inside
 * snippets/block-vip-pricing.liquid.
 *
 * CONFIRMED LIVE (tested directly against the Wholesale product page):
 * this theme's native "Add to cart" opens the header cart panel
 * in-place (no page navigation) when the actual submit BUTTON is
 * clicked. Firing the form's `submit` event directly (jQuery's
 * `$(form).trigger('submit')`, or `form.submit()`) skips that handler
 * and falls through to a plain HTML form submission — a full POST that
 * redirects to /cart. So step one is: inject the membership product as
 * an extra line item into the real add-to-cart form, then click the
 * form's own submit BUTTON so the native handler runs.
 *
 * Step two — updating the VIP pricing block and the product's own
 * displayed price WITHOUT a page reload:
 *
 * Both blocks decide their state from server-rendered Liquid (whether
 * the VIPMEMBERSHIP-tagged product is present in `cart.items`), and
 * Liquid only runs server-side. To reflect a cart change without a
 * reload, we use Shopify's built-in "section rendering" endpoint:
 * fetching the current URL with `?sections=<section-id>` returns fresh
 * HTML for just that one section, computed with the CURRENT cart state
 * — same Liquid, same logic, no reload. We then parse that HTML and
 * swap only the two blocks we care about into the live page, leaving
 * everything else (image gallery state, open panels, etc.) untouched.
 *
 * The section ID itself is read from our own price block's
 * `data-section-id` attribute (block-price.liquid already sets
 * `data-section-id="{{ section.id }}"`), so nothing is hardcoded.
 *
 * Verified live: after adding the membership, fetching
 * `?sections=<id>` and swapping in the fresh `[data-block-vip-pricing]`
 * and `block-price` elements correctly reflects the updated cart state
 * with no reload and no flicker of the rest of the page.
 */
(function () {
  'use strict';

  function findAddToCartForm() {
    return document.querySelector('form[action*="/cart/add"]');
  }

  function onAddMembershipClick(event) {
    var button = event.currentTarget;
    var membershipVariantId = button.getAttribute('data-membership-variant-id');
    var errorEl = null;
    var blockEl = button.closest('[data-block-vip-pricing]');
    if (blockEl) {
      errorEl = blockEl.querySelector('[data-add-membership-error]');
    }

    if (!membershipVariantId) {
      return;
    }

    var form = findAddToCartForm();
    var nativeSubmitButton = form ? form.querySelector('button[type="submit"]') : null;

    if (!form || !nativeSubmitButton) {
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.textContent = "Couldn't find the add-to-cart button on this page. Please try again or contact us.";
      }
      return;
    }

    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    button.disabled = true;
    var labelEl = button.querySelector('[data-button-label]');
    var originalLabel = labelEl ? labelEl.textContent : null;
    if (labelEl) {
      labelEl.textContent = 'Adding…';
    }

    // Remove any stray injected field from a previous attempt.
    form.querySelectorAll('[data-vip-injected]').forEach(function (el) {
      el.remove();
    });

    // Add the membership product as an extra line item. The form's own
    // `id` / `quantity` fields already represent the product page's
    // current variant and are left untouched, so this adds BOTH items
    // together in the one request.
    var idInput = document.createElement('input');
    idInput.type = 'hidden';
    idInput.name = 'items[0][id]';
    idInput.value = membershipVariantId;
    idInput.setAttribute('data-vip-injected', 'true');

    var qtyInput = document.createElement('input');
    qtyInput.type = 'hidden';
    qtyInput.name = 'items[0][quantity]';
    qtyInput.value = '1';
    qtyInput.setAttribute('data-vip-injected', 'true');

    form.appendChild(idInput);
    form.appendChild(qtyInput);

    // Click the theme's own submit button — NOT form.submit() and NOT
    // $(form).trigger('submit') — so its native click-bound handler
    // runs and adds both items via the theme's normal AJAX cart call.
    nativeSubmitButton.click();

    // Give the AJAX add a moment to actually reach Shopify's servers
    // (it's an async network request the click kicks off), then
    // refresh just our two blocks in place.
    setTimeout(function () {
      refreshBlocksInPlace(button, labelEl, originalLabel, errorEl);
    }, 900);
  }

  function refreshBlocksInPlace(button, labelEl, originalLabel, errorEl) {
    var priceBlock = document.querySelector('block-price[data-section-id]');
    var sectionId = priceBlock ? priceBlock.getAttribute('data-section-id') : null;

    if (!sectionId) {
      // No section id available — nothing more we can do without a
      // reload, but the cart add itself already succeeded, so just
      // restore the button rather than leaving it stuck on "Adding…".
      restoreButton(button, labelEl, originalLabel);
      return;
    }

    var url = window.location.pathname + '?sections=' + encodeURIComponent(sectionId);

    fetch(url)
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        var html = data[sectionId];
        if (!html) {
          throw new Error('Section response missing expected data.');
        }

        var doc = new DOMParser().parseFromString(html, 'text/html');

        var freshVipBlock = doc.querySelector('[data-block-vip-pricing]');
        var freshPriceBlock = doc.querySelector('block-price');

        var currentVipBlock = document.querySelector('[data-block-vip-pricing]');
        var currentPriceBlock = document.querySelector('block-price');

        if (freshVipBlock && currentVipBlock) {
          currentVipBlock.replaceWith(freshVipBlock);
          // The freshly-swapped-in block has its own "Add" button (if
          // still in the non-member state) that needs its click
          // listener re-attached, since replaceWith() drops all JS
          // event bindings on the old node.
          bindButtons(freshVipBlock);
        }
        if (freshPriceBlock && currentPriceBlock) {
          currentPriceBlock.replaceWith(freshPriceBlock);
        }

        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      })
      .catch(function () {
        // Section refresh failed for some reason (network hiccup, etc).
        // The cart add itself already went through, so fall back to a
        // full reload as a last resort rather than leaving the page in
        // a stale, unclear state.
        window.location.reload();
      })
      .finally(function () {
        // If the block wasn't replaced (e.g. error path already
        // reloading), this is a no-op since the button node itself may
        // no longer be in the document.
        restoreButton(button, labelEl, originalLabel);
      });
  }

  function restoreButton(button, labelEl, originalLabel) {
    if (!button || !button.isConnected) {
      return;
    }
    button.disabled = false;
    if (labelEl && originalLabel) {
      labelEl.textContent = originalLabel;
    }
  }

  function bindButtons(scope) {
    var root = scope || document;
    var buttons = root.querySelectorAll('[data-add-membership-button]');
    buttons.forEach(function (button) {
      button.addEventListener('click', onAddMembershipClick);
    });
  }

  function init() {
    bindButtons(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();